import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BackgroundGradient from "@/components/landing/BackgroundGradient";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { FeaturedArticle } from "@/components/blog/FeaturedArticle";
import { TagFilter } from "@/components/blog/TagFilter";
import { getAllArticles, getAllTags, getFeaturedArticle } from "@/lib/blog";
import type { Language } from "@/types/blog";

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const language: Language = i18n.language === "he" ? "he" : "en";
  const dir = language === "he" ? "rtl" : "ltr";
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const featured = useMemo(() => getFeaturedArticle(language), [language]);
  const allArticles = useMemo(() => getAllArticles(language), [language]);
  const tags = useMemo(() => getAllTags(language), [language]);

  const visibleArticles = useMemo(() => {
    const pool = featured
      ? allArticles.filter((a) => a.frontmatter.slug !== featured.frontmatter.slug)
      : allArticles;
    if (!selectedTag) return pool;
    return pool.filter((a) => a.frontmatter.tags.includes(selectedTag));
  }, [allArticles, featured, selectedTag]);

  return (
    <>
      <SEO
        path="/blog"
        title={t("pages.blog.metaTitle")}
        description={t("pages.blog.metaDescription")}
      />
      <BackgroundGradient />
      <div dir={dir} style={{ position: "relative", zIndex: 1, minHeight: "100vh", color: "var(--dyna-fg)" }}>
        <Navbar activePage="blog" />
        <main style={{ padding: "120px clamp(20px, 5vw, 80px) 80px", maxWidth: 1200, margin: "0 auto" }}>
          <header style={{ marginBottom: "var(--s-lg)" }}>
            <h1
              style={{
                fontFamily: "\"Coolvetica\", system-ui, sans-serif",
                fontSize: "clamp(40px, 6vw, 72px)",
                lineHeight: 1.05,
                textTransform: "uppercase",
                letterSpacing: "0.01em",
                color: "var(--dyna-fg)",
                margin: "0 0 12px",
              }}
            >
              {t("pages.blog.heading")}
            </h1>
            <p
              style={{
                fontFamily: "Geist, system-ui, sans-serif",
                fontSize: "clamp(16px, 1.5vw, 19px)",
                lineHeight: 1.6,
                color: "var(--dyna-fg-muted)",
                maxWidth: 720,
                margin: 0,
              }}
            >
              {t("pages.blog.subheading")}
            </p>
          </header>

          {featured && (
            <div style={{ marginBottom: "var(--s-lg)" }}>
              <FeaturedArticle article={featured} />
            </div>
          )}

          {tags.length > 0 && (
            <div style={{ marginBottom: "var(--s-md)" }}>
              <TagFilter tags={tags} selected={selectedTag} onSelect={setSelectedTag} />
            </div>
          )}

          {visibleArticles.length === 0 ? (
            <p
              style={{
                fontFamily: "Geist, system-ui, sans-serif",
                fontSize: 16,
                color: "var(--dyna-fg-muted)",
                padding: "var(--s-lg) 0",
                textAlign: "center",
              }}
            >
              {t("pages.blog.empty")}
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 24,
              }}
            >
              {visibleArticles.map((a) => (
                <ArticleCard key={a.frontmatter.slug} article={a} />
              ))}
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}
